import i18n from '@/utils/i18n';
import { captureException } from '@/utils/sentry';
import {
  parsePriceToMicros,
  parseQuantity,
} from '@/experiences/sales-portal/domain/pricing/screens/quotes/blocs/utils/parse-quote-input';
import { partnerSupplier } from '@/utils/environment';
import { PriceQuotesSavedListDocument } from '@/experiences/sales-portal/domain/pricing/screens/quotes/blocs/documents/price-quotes-saved-list.generated';
import {
  QuoteAction,
  QuoteState,
} from '@/experiences/sales-portal/domain/pricing/screens/quotes/blocs/hooks/use-quote-state-types';
import { SavePriceQuoteLineItemInput } from '@/types';
import { useCallback, useRef } from 'react';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';
import { useSavePriceQuoteMutation } from '@/experiences/sales-portal/domain/pricing/screens/quotes/blocs/documents/save-price-quote.generated';

interface UseSavePriceQuoteMutatorArgs {
  dispatch: React.Dispatch<QuoteAction>;
  state: QuoteState;
}

interface SaveOptions {
  /** Submit this instead of state.name — avoids the stateRef vs render race when save() runs in the same tick as dispatch(SET_NAME). */
  name?: string;
  /** Skip the success toast (e.g., when chaining save into another action that owns the user feedback). */
  silent?: boolean;
}

interface UseSavePriceQuoteMutatorResult {
  save: (options?: SaveOptions) => Promise<boolean>;
  saving: boolean;
}

/**
 * Calls the `savePriceQuote` mutation with the current editor state. On
 * success, dispatches `SAVE_SUCCESS` to adopt the server-assigned quote id;
 * the dirty flag is cleared only when the editor hasn't changed during the
 * round-trip. On error, fires an error toast and leaves state untouched.
 *
 * `quote_type` is derived from `state.competitorName`: `'COMPETITIVE'` when
 * a competitor is named, otherwise `'STANDARD'`. Per-line `competitor`
 * payloads are only sent in competitive mode.
 *
 * State is read through a ref so `save` keeps a stable reference across
 * renders — callers can pass it to `useEffect`/`useCallback` deps without
 * re-firing on every keystroke.
 *
 * @param args - Mutator arguments
 * @param args.dispatch - Quote-state reducer dispatch
 * @param args.state - Current quote-editor state
 * @returns `{ save, saving }` — `save` triggers the mutation; `saving` is
 *   the in-flight flag for the Save button.
 */
export const useSavePriceQuoteMutator = ({
  dispatch,
  state,
}: UseSavePriceQuoteMutatorArgs): UseSavePriceQuoteMutatorResult => {
  const { showErrorToast, showToast } = useExperienceToasts();
  const [savePriceQuote, { loading: saving }] = useSavePriceQuoteMutation();

  // Latest state lives in a ref so the `save` callback is referentially
  // stable. Reading state through closure deps would re-create `save` on
  // every keystroke, defeating any consumer that relies on its identity.
  const stateRef = useRef(state);
  stateRef.current = state;

  const save = useCallback(
    async (options?: SaveOptions): Promise<boolean> => {
      const submitted = stateRef.current;
      // `canSave` already gates this on the screen — guard kept as a TS narrow
      // (so `submitted.account.chatId` below type-checks) and a defensive
      // fallback if anything ever invokes `save` outside the disabled-button path.
      if (submitted.account === null) {
        return false;
      }

      const submittedName = options?.name ?? submitted.name;
      const isCompetitive = submitted.competitorName !== null;

      const inputLineItems: SavePriceQuoteLineItemInput[] =
        submitted.lineItems.map((item) => ({
          // Backend Pydantic requires the key present even though GraphQL allows null.
          competitor:
            isCompetitive && item.competitor
              ? {
                  item_name: item.competitor.itemName,
                  pack_price_in_micros: parsePriceToMicros(
                    item.competitor.price,
                  ),
                  pack_size: item.competitor.packSize ?? null,
                }
              : null,
          item_code: item.itemCode ?? null,
          item_description: item.itemDescription ?? null,
          item_name: item.itemName,
          item_pack_size: item.itemPackSize ?? null,
          pack_price_in_micros: parsePriceToMicros(item.ourPrice),
          quantity: parseQuantity(item.quantity),
          variant_pack_id: item.variantPackId ?? null,
        }));

      try {
        const { data } = await savePriceQuote({
          // refetch keeps the Load Quotes dropdown in sync after save
          refetchQueries: [
            {
              query: PriceQuotesSavedListDocument,
              variables: {
                businessOrganizationUUID:
                  partnerSupplier.businessOrganizationUUID,
              },
            },
          ],
          variables: {
            chat_id: submitted.account.chatId,
            competitor_name: submitted.competitorName,
            line_items: inputLineItems,
            name: submittedName,
            price_quote_id: submitted.quoteId,
            quote_type: isCompetitive ? 'COMPETITIVE' : 'STANDARD',
          },
        });

        const result = data?.savePriceQuote;
        if (!result) {
          captureException(
            new Error('savePriceQuote returned a null payload (success path)'),
          );
          showErrorToast(i18n.t('sales_portal.price_quotes.save_error'));
          return false;
        }

        // Detect mid-flight edits: if any dirty-tracked field changed during the
        // round-trip, keep `isDirty` true and don't overwrite the local name.
        // Reducer mutations always produce new array/string identities, so a
        // shallow compare is sufficient.
        const current = stateRef.current;
        const hasStaleEdits =
          current.name !== submittedName ||
          current.lineItems !== submitted.lineItems ||
          current.competitorName !== submitted.competitorName;

        dispatch({
          name: hasStaleEdits ? current.name : result.name,
          preserveDirty: hasStaleEdits,
          quoteId: result.price_quote_id,
          type: 'SAVE_SUCCESS',
        });
        if (!options?.silent) {
          showToast({
            title: i18n.t('sales_portal.price_quotes.save_success'),
            variant: 'success',
          });
        }
        return true;
      } catch (error) {
        captureException(error);
        showErrorToast(i18n.t('sales_portal.price_quotes.save_error'));
        return false;
      }
    },
    [dispatch, savePriceQuote, showErrorToast, showToast],
  );

  return { save, saving };
};
